import type { Channel, Partner } from "@expert/shared-types";

export const NORMAL_WS_CLOSE_CODE = 1000;

export const WsEvents = {
    // Incoming events
    TRANSCRIPT: "transcript",
    AUDIO: "audio",
    END: "end",
    ERROR: "error",
};

//Incoming Event Types
export type AiRolePlayEvent = (typeof WsEvents)[keyof typeof WsEvents];

//Incoming events schema
export interface AiRolePlayEventPayload {
    event: AiRolePlayEvent;
    eventPayload: TranscriptEventPayload | AudioEventPayload | EndEventPayload;
    timestamp: number;
}

//Transcript event Payload schema
export interface TranscriptEventPayload {
    transcript: string;
    channel: string;
}

//Audio event Payload schema
export interface AudioEventPayload {
    audioBlob: string;
    channel: string;
}

//Incoming End event Payload schema
export interface EndEventPayload {
    reasonType: string; //Error or CallEnd
}

export type ExpertUniversityEvents = {
    "websocket-opened": { websocketUrl: string };
    "websocket-closed": { websocketUrl: string };
    "audio-event-received": { audioBlob: string };
    "start-simulation-training": {
        profileId: string;
        partner: Partner;
        liveKitConfiguration?: { isLiveKitSession?: boolean; isAscendProfile?: boolean };
        isSandbox?: boolean;
    };
    "starting-training-session": {};
    "training-session-started": {};
    "failed-to-start-training-session": {};
    /** Ready for Start on the pre-call interstitial. */
    "livekit-agent-ready": {};
    /** Expert confirmed Start — accept deferred task. */
    "join-livekit-call": {};
    /** Expert cancelled call start — tear down in-flight launch and return to tiles. */
    "cancel-livekit-call-start": {};
    "trigger-evaluation-results": { sessionId: string; profileId: string };
    "final-behavior-reached": {};
};

// Context
export interface ExpertUniversityConfigurationContextType {
    allowedPartners: string[];
    taskInSandbox: boolean;
    sandboxProfileId: string;
    mostRecentSessionId?: string;
    setTaskInSandbox: React.Dispatch<React.SetStateAction<boolean>>;
    setTrainingPartner: React.Dispatch<React.SetStateAction<string>>;
    setMostRecentSessionId: React.Dispatch<React.SetStateAction<string | undefined>>;
    trainingPartner: Partner;
}

//Template Types

/** `block` is 1-based into the learning-block titles and drives the accordion grouping; `blockSequence`
 *  orders profiles within a block. `block` is required whenever the config is present. */
export type LearningBlockConfig = {
    block: number;
    blockSequence?: number;
};

/** Minimal criterion shape returned on template list/detail APIs. */
export type EvaluationCriterion = {
    behavior: string;
};

/** Behaviors a profile is graded against. Presence in `criteria` is the selection list. */
export type EvaluationCriteria = {
    criteria: EvaluationCriterion[];
    defaultMatchMode?: string;
};

export type TemplateBase = {
    id: string;
    /** @deprecated Superseded by {@link learningBlockConfig}. Still read as a fallback for profiles
     *  saved before the config field existed. */
    learningBlock?: number;
    learningBlockConfig?: LearningBlockConfig;
    description: string;
    partner: AllowedPartners;
    status: TemplateStatus;
    lob: LineOfBusiness;
    customerIdentifier: string;
    simulationId?: string;
    /**
     * Behavior IDs projected from evaluation criteria.
     * Supplied by `GET .../templates/{tenant}/{partner}` (list) so clients do not need per-profile detail fetches.
     * Used by the Ascend pre-call interstitial for assumed-done context.
     */
    behaviors?: string[];
};

//TODO: As partners are enabled, add them to this array
export const EnabledPartners = ["verizon", "att", "amazonrouter"] as const;

export const BlockedChannels: readonly Channel[] = ["chat"];

export type AllowedTenants = "asurion";
export type AllowedPartners = (typeof EnabledPartners)[number];
export type LineOfBusiness = "soluto" | "mobility" | "care";

export const TemplateStatus = Object.freeze({
    IN_PROGRESS: "IN_PROGRESS",
    IN_PRODUCTION: "IN_PRODUCTION",
});
export type TemplateStatus = (typeof TemplateStatus)[keyof typeof TemplateStatus];
